import { MEDIA_STALE_TIME } from '@/utils/media';
import {
    MediaProgramMetadata,
    MediaGuideMetadata,
    MediaGuideWithSubtitles,
} from '@/utils/media/types';
import { useCircle } from '@rhg/hooks/use-circle';
import createMediaV2Api from '@rhg/libs/api/create-media-v2-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    checkQueryCache,
    ItemToQueryMapping,
    keepPreviousData,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useMemo } from 'react';
import { useActiveCircleId } from '../use-active-circle';
import { cacheGuides } from '../use-guides';
import { cacheProgramMetadata } from './cache';

export const programMetadataQueryOptions = {
    queryKey: ({
        programIds,
        circleId,
    }: {
        programIds: Set<string>;
        circleId: number;
    }) => {
        return [
            queryKeys.programMetadatas,
            circleId,
            Array.from(programIds).reduce(
                (prev, programId) => ({
                    ...prev,
                    [programId]: programId,
                }),
                {} as Record<string, string>
            ),
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        guideIds,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        guideIds: Set<string>;
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) => {
        const mediaV2Api = createMediaV2Api(getAccessTokenSilently);

        const response = await mediaV2Api.v2GetGuides({
            circleAuthorizationToken,
            guideId: Array.from(guideIds),
            recipientId: circleId,
            rHGApp,
        });

        return response.data.programs as Array<
            MediaProgramMetadata & { guides: MediaGuideWithSubtitles[] }
        >;
    },
    queryOptions: ({
        guideIds,
        programIds,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        guideIds: Set<string>;
        programIds: Set<string>;
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) => {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                guideIds.size > 0 &&
                programIds.size > 0
            ),
            staleTime: MEDIA_STALE_TIME,
        };
    },
};

export function usePackGuideProgramMetadata(
    packGuides: Pick<MediaGuideMetadata, 'guideId' | 'programId'>[] = [],
    { placeholderData }: { placeholderData?: typeof keepPreviousData } = {}
) {
    const { activeCircleId: circleId } = useActiveCircleId();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = programMetadataQueryOptions;
    const rHGApp = getRhgAppHeader();
    const guideIds = useMemo(
        () => new Set(packGuides.map((g) => g.guideId)),
        [packGuides]
    );
    const programIds = useMemo(
        () => new Set(packGuides.map((g) => g.programId)),
        [packGuides]
    );

    const options = useMemo(
        () =>
            queryOptions({
                guideIds,
                programIds,
                isAuthenticated,
                circleId,
                circleAuthorizationToken,
            }),
        [
            circleAuthorizationToken,
            circleId,
            guideIds,
            programIds,
            isAuthenticated,
            queryOptions,
        ]
    );

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({
            programIds,
            circleId,
        }),
        queryFn: async () => {
            if (!programIds.size) return [];
            const _programIdsArray = Array.from(programIds);

            const keyObjects = _programIdsArray.map(
                (programId): ItemToQueryMapping<string> => ({
                    item: programId,
                    queryKey: queryKey({
                        programIds: new Set([programId]),
                        circleId,
                    }),
                })
            );

            const { cachedQueries, invalidQueries } = checkQueryCache<
                string,
                MediaProgramMetadata[]
            >(keyObjects, options.staleTime);
            const cachedQueriesData = cachedQueries.reduce<
                MediaProgramMetadata[]
            >((cachedData, item) => [...cachedData, ...item.data], []);

            // If all program metadatas are already fetched and not stale, return them
            if (invalidQueries.length === 0) {
                return cachedQueriesData;
            }

            // Find the associated guides to fetch
            const guideIdsToFetch = packGuides
                .filter((g) =>
                    invalidQueries.some(({ item }) => item === g.programId)
                )
                .map((g) => g.guideId);
            const response = await queryFn({
                getAccessTokenSilently,
                guideIds: new Set(guideIdsToFetch),
                circleId,
                circleAuthorizationToken,
                rHGApp,
            });

            // populate cache for program metadata
            const programs = response ?? [];
            cacheProgramMetadata(programs, circleId);
            // populate cache for each guide
            const guides = programs
                .flatMap((p) => p.guides)
                .filter((g) => typeof g !== 'undefined');
            cacheGuides(guides, circleId);

            // Combine newly fetched packs with existing packs
            return [...cachedQueriesData, ...(programs || [])];
        },
        ...options,
        placeholderData,
    });

    return {
        programMetadatas: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}
